import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { NORNE_CENTERID } from "@portal/assets/banks";
import useAmlVerified from "../useAmlVerified";
import useElectronicIdentification from "../useElectronicIdentification";
import useIsCorporateUser from "../useIsCorporateUser";
import { IsNorne } from "@portal/helpers/IsNorne";
import useAdvisor from "../useAdvisor";
import { trackMessage } from "@portal/appInsights";
import useIsCenterIdCustomer from "../useIsCenterIdCustomer";
import { useSession } from "@portal/auth/client/SessionProvider";
import { IUseVerifyCustomerAndAmlStatus } from "./types";

const useVerifyCustomerAndAmlStatus: IUseVerifyCustomerAndAmlStatus = (ignoreAmlAndIdentityRedirect = false) => {
  const router = useRouter();
  const pathname = usePathname();

  const { isAdvisor } = useAdvisor();
  const { session } = useSession();
  const { data: isCenterIdCustomer, error } = useIsCenterIdCustomer(session?.centerId);
  const { data: isCustomer, error: isCustomerError } = useIsCenterIdCustomer("any");

  const { data: eIdentityOk, isLoading: eIdentityLoading, error: eIdentityError } = useElectronicIdentification();
  const { amlVerified, redirectUrl: amlRedirectUrl } = useAmlVerified();
  const { isCorporateUser } = useIsCorporateUser();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (
      session?.status !== "authenticated" ||
      isCenterIdCustomer === undefined ||
      isCenterIdCustomer === null ||
      isCustomer === undefined ||
      isCustomerError === null ||
      isAdvisor ||
      !session ||
      error
    ) {
      setIsLoading(false);
      return;
    }
    if (!isCustomer) {
      trackMessage(
        `Not a customer, redirecting to onboarding. status=${session?.status}, isCustomer=${isCustomer}, pathname=${pathname}. centerId=${session?.centerId}`,
      );
      const onboardingBaseUrl = IsNorne(session?.centerId ?? NORNE_CENTERID)
        ? process.env.NEXT_PUBLIC_ONBOARDING_NORNE_URL
        : isCorporateUser
          ? process.env.NEXT_PUBLIC_ONBOARDING_COMPANY_URL
          : process.env.NEXT_PUBLIC_ONBOARDING_PRIVATE_URL;
      setIsLoading(false);
      router.push(
        `${onboardingBaseUrl}?centerid=${session?.centerId ?? NORNE_CENTERID}&returnUrl=${
          process.env.NEXT_PUBLIC_APPLICATION_BASE_URL
        }${pathname}`,
      );
    } else if (!isCenterIdCustomer) {
      trackMessage(
        `Not a customer of current centerId, but customer of Norne or other bank, redirecting to bli-kunde. status=${session?.status}, isCustomer=${isCustomer}, pathname=${pathname}. centerId=${session?.centerId}`,
      );
      setIsLoading(false);
      router.push(`/bli-kunde?currentPath=${pathname}`);
    } else if (!ignoreAmlAndIdentityRedirect) {
      if (amlRedirectUrl) {
        router.push(amlRedirectUrl);
      } else if (!eIdentityLoading && !eIdentityError && !eIdentityOk) {
        router.push(
          `${process.env.NEXT_PUBLIC_ELECTRONIC_IDENTIFICATION_URL}?returnUrl=${process.env.NEXT_PUBLIC_APPLICATION_BASE_URL}${pathname}`,
        );
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [
    router,
    amlVerified,
    pathname,
    isCenterIdCustomer,
    eIdentityLoading,
    eIdentityError,
    eIdentityOk,
    session,
    isCorporateUser,
    amlRedirectUrl,
    session?.status,
    isAdvisor,
    error,
    isCustomer,
    isCustomerError,
    ignoreAmlAndIdentityRedirect,
  ]);

  return { isLoading };
};

export default useVerifyCustomerAndAmlStatus;
